import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  untracked,
  viewChild
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { Chart } from 'chart.js';

import { ChartJsData, ChartType, DataSet } from '@ui/shared/model';

import { LoadingSpinnerComponent } from '../../../legacy/loading-spinner/loading-spinner.component';
import { ChartJsService } from './services/chart-js/chart-js.service';

@Component({
  selector: 'app-card-graph',
  templateUrl: './card-graph.component.html',
  styleUrls: ['./card-graph.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingSpinnerComponent]
})
export class CardGraphComponent implements OnDestroy {
  private translate = inject(TranslateService);
  private chartJsService = inject(ChartJsService);

  readonly graphData = input<ChartJsData>();
  readonly graphType = input<string>();
  readonly interval = input<string>();
  readonly loading = input<boolean>(false);
  readonly hideLegend = input<boolean>(false);
  readonly graphCanvas = viewChild<ElementRef>('graphCanvas');
  public chart: Chart;

  private graphCanvasEffect = effect(() => {
    const _canvas = this.graphCanvas(); // this is the trigger of the effect, do not remove it!
    untracked(() => {
      const graphType = this.graphType();
      const graphData = this.graphData();
      if (graphData && graphData.datasets && graphType) {
        this.reDrawChart(graphData, graphType, this.interval());
      }
    });
  });

  private graphDataEffect = effect(() => {
    const graphData = this.graphData();
    untracked(() => {
      if (graphData) {
        this.reDrawChart(graphData, this.graphType(), this.interval());
      }
    });
  });

  ngOnDestroy(): void {
    if (this.chart) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.chart.destroy();
    }
  }

  private reDrawChart(
    graphData: ChartJsData,
    chartType: string,
    interval: string
  ) {
    if (this.chart) {
      // maybe use this.chart.update();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.chart.destroy();
    }
    if (this.graphCanvas() && this.graphType()) {
      this.drawChart(this.graphCanvas(), graphData, chartType, interval);
    }
  }

  private drawChart(
    elRef: ElementRef,
    graphData: ChartJsData,
    chartType: string,
    interval: string
  ) {
    const yMaxValue = this._getMaxValue(graphData.datasets);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.chart = new Chart(elRef.nativeElement, {
      type: chartType,
      data: this.getChartData(graphData, chartType, elRef.nativeElement),
      ...this.chartJsService.getChartConfig(
        chartType,
        interval,
        yMaxValue,
        this.hideLegend()
      )
    });
  }

  public _getMaxValue(datasets: DataSet) {
    const keys = Object.keys(datasets);
    const ret = [];
    keys.forEach(key => {
      ret.push(Math.max(...datasets[key].dataSet));
    });
    return Math.max(...ret);
  }

  private getChartData(
    graphData: ChartJsData,
    chartType: string,
    canvas: HTMLCanvasElement
  ) {
    const labels = this.getLabels(chartType, graphData);
    const dataset = this._setGraphDataSet(
      canvas,
      graphData.datasets,
      chartType
    );
    return {
      labels: labels,
      datasets: dataset
    };
  }

  private getLabels(chartType: string, graphData: ChartJsData) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return chartType === ChartType.PIE
      ? Object.values(graphData.datasets).map(v =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          this.translate.instant(v['label'])
        )
      : [...graphData.labels];
  }

  public _setGraphDataSet(
    canvas: HTMLCanvasElement,
    datasets: DataSet,
    chartType: string
  ) {
    if (chartType === ChartType.PIE) {
      return [
        {
          backgroundColor: Object.keys(datasets).map(key => {
            const { color } = datasets[key];
            return color;
          }),
          data: Object.keys(datasets).map(key => {
            const { dataSet } = datasets[key];
            return dataSet.reduce((a, b) => a + b, 0);
          })
        }
      ];
    }
    return Object.keys(datasets).map(key => {
      const { color, label, dataSet } = datasets[key];
      const gradientStroke = canvas
        .getContext('2d')
        .createLinearGradient(0, 0, canvas.width, 0);
      gradientStroke.addColorStop(0, color);
      return this.getDataSetEntry(chartType, label, dataSet, gradientStroke);
    });
  }

  private getDataSetEntry(
    chartType: string,
    label: string,
    data: number[],
    color: CanvasGradient
  ) {
    if (chartType === ChartType.LINE) {
      return {
        label: this.translate.instant(label),
        data: [...data],
        backgroundColor: 'transparent',
        borderColor: color,
        pointBackgroundColor: 'white',
        pointBorderWidth: 2,
        pointRadius: 4
      };
    } else if (chartType === ChartType.BAR) {
      return {
        label: this.translate.instant(label),
        data: [...data],
        backgroundColor: color,
        maxBarThickness: 30
      };
    }
  }
}
